import { useCallback, useMemo, useRef } from "react";
import { Button, Form } from "reactstrap";
import { Action } from "../../../types/reducer-action";
import { equals } from "../../../utils/commons";
import AccordionContainer from "../../common/accordion-container";
import BusyButton from "../../common/busy-button";
import ChangeAlert, { ChangeAlertRef } from "../../common/change-alert";
import { DataEntryEvent } from "../../common/data-entry-reducer";
import { RefreshIcon, SaveIcon } from "../../common/icons";
import './style.scss';
import useUserApi from "./useUserApi";
import UserInfoPanel from "./user-info-panel";
import { useUserDispatch, useUserState } from "./user-manager-context";
import UserRolesPanel from "./user-roles-panel";

export default function UserProfileTab() {
  const { dataDetails, form } = useUserState();

  const dispatch = useUserDispatch();

  const { updateUser, busy } = useUserApi();

  const changeAlertRef = useRef<ChangeAlertRef>(null);

  const reset = () => dispatch(new Action(DataEntryEvent.ResetForm));

  const isDirty = useMemo(() => (
    !!dataDetails && (!equals(form.individualId, dataDetails.individualId)
      || !equals(form.email, dataDetails.email)
      || !equals(form.expired, dataDetails.expired)
      || !equals(form.assignment?.id, dataDetails.assignment?.id)
    )
  ), [dataDetails, form]);

  const validate = useCallback(() => {
    let ok = true;

    if (form.individualId < 0) {
      dispatch(new Action(DataEntryEvent.AddErrors,
        { individualName: 'Invalid user.' }
      ));
      ok = false;
    }

    return ok;
  }, [dispatch, form.individualId]);

  const save = useCallback(() => {
    form?.id && updateUser(form?.id, {
      ...form,
      individualId: equals(dataDetails?.individualId, form.individualId) ? 0 : form.individualId // 0: no update on individualId
    }).then(result => changeAlertRef.current?.complete(!!result));
  }, [dataDetails, form, updateUser]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    validate() && save();
  };

  return (<>
    <AccordionContainer title="User Information" titleClass="fs-6">
      <ChangeAlert isChanged={isDirty} isOpen={isDirty}
        itemName={`${form?.firstName} ${form?.lastName}'s profile`}
        onDiscard={reset}
        ref={changeAlertRef} />

      <Form onSubmit={handleSubmit} onReset={reset} className="oms-container">
        <UserInfoPanel wide />

        <div className="oms-row">
          <BusyButton type="submit" color="primary" aria-label="Save"
            disabled={!isDirty}
            value="Save" busy={busy}>
            <SaveIcon />
          </BusyButton>

          <Button type="reset" color="secondary" aria-label="Reset"
            disabled={busy}>
            <RefreshIcon /> Reset
          </Button>
        </div>
      </Form>
    </AccordionContainer>

    <AccordionContainer title="User Roles" titleClass="fs-6">
      <UserRolesPanel />
    </AccordionContainer>
  </>);
}
